import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, Camera, FileText, Loader2, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || "";

const CATEGORIES = {
  fuel: "Fuel",
  repairs: "Repairs",
  insurance: "Insurance",
  subscriptions: "Subscriptions",
  cpd: "CPD / Training",
  parking: "Parking / Tolls",
  vehicle_finance: "Vehicle Finance",
  other: "Other",
};

const pounds = (pence) => `£${((Number(pence) || 0) / 100).toFixed(2)}`;

function receiptHref(value) {
  if (!value) return null;
  if (/^https?:\/\//i.test(value)) return value;
  return `${API}${value.startsWith("/") ? "" : "/"}${value}`;
}

/**
 * Receipt History is the human-facing record of purchases, regardless of
 * capture method. Voice/text entries and photographed receipts are all
 * instructor_expenses; receipt_url is optional evidence, not a different
 * kind of history.
 */
export default function ReceiptHistory() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/api/instructor-freedom/expenses`, {
        withCredentials: true,
      });
      setItems(Array.isArray(data?.items) ? data.items : []);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't load your receipts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="receipt-history-page" className="min-h-dvh bg-slate-50 dark:bg-[#0c1120] pb-24">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto flex h-14 max-w-lg items-center gap-3 px-4">
          <button
            type="button"
            aria-label="Back"
            onClick={() => navigate(-1)}
            className="text-slate-500"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <FileText className="h-5 w-5 text-emerald-600" />
          <h1 className="flex-1 text-base font-extrabold text-slate-900 dark:text-white">
            Receipt History
          </h1>
          <button type="button" aria-label="Refresh receipts" onClick={load} className="text-slate-500">
            <RefreshCw className="h-5 w-5" />
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-lg space-y-3 px-4 pt-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Every receipt you record, whether spoken, typed or photographed.
        </p>

        {loading && (
          <div className="flex justify-center py-10">
            <Loader2 className="h-7 w-7 animate-spin text-emerald-600" />
          </div>
        )}

        {!loading && error && (
          <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div data-testid="receipt-history-empty" className="rounded-2xl border border-slate-200 bg-white p-5 text-sm text-slate-600 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-300">
            No receipts recorded yet.
          </div>
        )}

        {!loading && !error && items.map((item) => {
          const href = receiptHref(item.receipt_url);
          return (
            <article
              key={item.id}
              data-testid={`receipt-item-${item.id}`}
              className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900"
            >
              <div className="flex items-start gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${href ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  {href ? <Camera className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="font-bold text-slate-900 dark:text-white">
                      {CATEGORIES[item.category] || item.category || "Receipt"}
                    </p>
                    <p className="text-base font-extrabold text-slate-900 dark:text-white">
                      {pounds(item.amount_pence)}
                    </p>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{item.date}</p>
                  {item.notes && <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{item.notes}</p>}
                  <p className="mt-2 text-xs font-semibold text-slate-500">
                    {href ? "Receipt image attached" : "Recorded without a photo"}
                  </p>
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-sm font-bold text-emerald-700 underline"
                    >
                      View receipt
                    </a>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </main>
    </div>
  );
}
